import React, { useMemo } from 'react';
import { Button, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { IconHomeOff } from '@tabler/icons-react';
import { CORAL, CORAL_SOFT_BG, TEAL } from '../../theme/creamyTokens';
import GearSpinner from '../../components/GearSpinner';

const { Title, Paragraph } = Typography;

// Ambient scenery, not a loading state: a scatter of gears turning slowly
// behind the CTA so the blank half of the screen reads as alive rather than
// as empty.
//
// This used to be the same rising-bubble field Explorar's loading state
// used -- spa/water language inherited from the jacuzzi app this was built
// out of, and the last decorative trace of it. Gears do the same job here
// and belong to the machinery this screen is about.
//
// Nothing rises any more, which is the point: rising reads as progress, and
// there is none to report on an empty list. They just turn, at their own
// unhurried pace and in both directions, so no two line up.
const GEAR_COUNT = 7;
const MIN_SIZE = 26;
const MAX_SIZE = 64;
const MIN_DURATION_S = 9;
const MAX_DURATION_S = 18;
const MIN_OPACITY = 0.1;
const MAX_OPACITY = 0.2;
// Kept off the very edges so a gear is never half-cropped by the container.
const MIN_INSET_PERCENT = 4;
const MAX_INSET_PERCENT = 78;

interface AmbientGear {
  id: number;
  leftPercent: number;
  topPercent: number;
  size: number;
  color: string;
  opacity: number;
  duration: number;
  reverse: boolean;
}

// True randomness per mount, same reasoning the bubble field had before it:
// an evenly spaced scatter reads as a pattern, and a pattern is exactly
// what ambient scenery must not be.
const buildGears = (): AmbientGear[] =>
  Array.from({ length: GEAR_COUNT }, (_, i) => ({
    id: i,
    leftPercent: MIN_INSET_PERCENT + Math.random() * (MAX_INSET_PERCENT - MIN_INSET_PERCENT),
    topPercent: MIN_INSET_PERCENT + Math.random() * (MAX_INSET_PERCENT - MIN_INSET_PERCENT),
    size: MIN_SIZE + Math.random() * (MAX_SIZE - MIN_SIZE),
    color: i % 2 === 0 ? CORAL : TEAL,
    opacity: MIN_OPACITY + Math.random() * (MAX_OPACITY - MIN_OPACITY),
    duration: MIN_DURATION_S + Math.random() * (MAX_DURATION_S - MIN_DURATION_S),
    reverse: i % 2 === 1,
  }));

interface MySpacesEmptyStateMobileProps {
  title: string;
  description: string;
  buttonLabel: string;
  onCreate: () => void;
}

/**
 * Mobile-only "no spaces yet" state for MyHeavyDutyMachineries -- replaces the
 * generic <EmptyState> (kept as-is for desktop) with the app's own coral
 * pill CTA instead of antd's default black square button, plus a scatter of
 * slowly turning gears below it (see buildGears above) so the blank space
 * reads as alive instead of just empty.
 */
const MySpacesEmptyStateMobile: React.FC<MySpacesEmptyStateMobileProps> = ({
  title,
  description,
  buttonLabel,
  onCreate,
}) => {
  const gears = useMemo(buildGears, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ textAlign: 'center', padding: '24px 16px 0' }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: CORAL_SOFT_BG,
            color: CORAL,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <IconHomeOff size={28} />
        </div>
        <Title level={4} style={{ fontWeight: 600, marginBottom: 8 }}>
          {title}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 20, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
          {description}
        </Paragraph>
        <Button type="primary" size="large" shape="round" icon={<PlusOutlined />} onClick={onCreate}>
          {buttonLabel}
        </Button>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 160,
          position: 'relative',
          overflow: 'hidden',
          marginTop: 16,
        }}
        aria-hidden="true"
      >
        {gears.map((gear) => (
          <GearSpinner
            key={gear.id}
            size={gear.size}
            durationSeconds={gear.duration}
            reverse={gear.reverse}
            style={{
              position: 'absolute',
              left: `${gear.leftPercent}%`,
              top: `${gear.topPercent}%`,
              color: gear.color,
              opacity: gear.opacity,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default MySpacesEmptyStateMobile;
